import {useState} from "react";
import {HistoryEntry} from "@/lib/types/models.ts";
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
    CardDescription,
} from "@/components/ui/card.tsx";
import {Separator} from "@/components/ui/separator.tsx";
import AuthorizedLayout from "@/layouts/AuthorizedLayout.tsx";
import PatientHistoryTable from "@/components/patients/PatientHistoryTable.tsx";
import {usePage} from "@inertiajs/react";
import {router} from "@inertiajs/core";
import CreateHistoryEntryDialog from "@/components/dialogs/CreateHistoryEntryDialog.tsx";
import EditHistoryEntryDialog from "@/components/dialogs/EditHistoryEntryDialog.tsx";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Plus} from "lucide-react";
import {HistoryEntryPayload, HistoryEntryUpdatePayload} from "@/lib/types/payloads.ts";
import PatientAttachments from "@/components/PatientAttachments.tsx";
import {PageTitle} from "@/components/PageTitle.tsx";

export default function PatientCardsShow() {
    const {patient_card, translations: t} = usePage().props as any;

    const [editingEntry, setEditingEntry] = useState<HistoryEntry | null>(null);
    const [deleteEntry, setDeleteEntry] = useState<HistoryEntry | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

    const patient = patient_card.patient;
    const entries = patient_card.history_entries;

    const handleCreate = async (data: HistoryEntryPayload) => {
        router.post("/history_entries", {history_entry: data as Record<string, any>});
        setIsCreateDialogOpen(false);
    };

    const handleUpdate = async (id: number, data: HistoryEntryUpdatePayload) => {
        router.patch(`/history_entries/${id}`, {history_entry: data as Record<string, any>});
        setIsEditDialogOpen(false);
    };

    const requestDelete = (entry: HistoryEntry) => setDeleteEntry(entry);

    return (
        <AuthorizedLayout>
            <div className="p-6 space-y-6 overflow-x-hidden">
                <PageTitle>
                    {`${t.title}: ${patient?.name ?? ""} (${t.label_record_number}: #${patient_card.record_number ?? "-"})`}
                </PageTitle>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <Card>
                            <CardHeader>
                                <CardTitle>{t.section_patient_info_title}</CardTitle>
                                <CardDescription>{t.section_patient_info_description ?? ""}</CardDescription>
                            </CardHeader>

                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_name}</p>
                                        <p className="font-medium truncate">{patient?.name ?? "-"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_phone}</p>
                                        <p className="font-medium">{patient?.phone_number ?? "-"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_city}</p>
                                        <p className="font-medium">{patient?.city ?? "-"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_address}</p>
                                        <p className="font-medium truncate">{patient?.address ?? "-"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_year_of_birth}</p>
                                        <p className="font-medium">{patient?.year_of_birth ?? "-"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_call_before ?? "Pozvati pre termina"}</p>
                                        <p className="font-medium">{patient?.call_before_appointment ? "Da" : "Ne"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_orthodontics ?? "Ortodontska terapija"}</p>
                                        <p className="font-medium">{patient?.orthodontics ? "Da" : "Ne"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_doctor ?? "Doktor"}</p>
                                        <p className="font-medium">{patient?.doctor?.name ?? "-"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_orthodontist ?? "Ortodont"}</p>
                                        <p className="font-medium">{patient?.orthodontist?.name ?? "-"}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">{t.label_xray ?? "RTG"}</p>
                                        <p className="font-medium">{patient?.xray ?? "-"}</p>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <p className="text-sm text-muted-foreground">{t.label_note ?? "Napomena"}</p>
                                    <div
                                        className="mt-2 p-4 bg-gray-50 border border-gray-100 rounded min-h-[6rem] prose max-w-none"
                                    >
                                        <p className="whitespace-pre-wrap text-sm text-gray-800">{patient?.note ?? "-"}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="lg:col-span-1">
                        <PatientAttachments
                            initialAttachments={patient_card.attachments || []}
                            patientCardId={patient_card.id}
                            translations={t}
                        />
                    </div>
                </div>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>{t.section_history_title}</CardTitle>
                        <Button size="icon" onClick={() => setIsCreateDialogOpen(true)}>
                            <Plus/>
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <Separator className="mb-4"/>
                        <PatientHistoryTable
                            entries={entries}
                            onDelete={requestDelete}
                            onEdit={(entry) => {
                                setEditingEntry(entry);
                                setIsEditDialogOpen(true);
                            }}
                        />
                    </CardContent>
                </Card>

                <CreateHistoryEntryDialog
                    isOpen={isCreateDialogOpen}
                    onOpenChange={setIsCreateDialogOpen}
                    onCreate={handleCreate}
                    patientCardId={patient_card.id}
                />

                {editingEntry && (
                    <EditHistoryEntryDialog
                        isOpen={isEditDialogOpen}
                        onOpenChange={setIsEditDialogOpen}
                        onUpdate={(data) => handleUpdate(editingEntry.id, data)}
                        entry={editingEntry}
                    />
                )}

                <ConfirmDialog
                    isOpen={!!deleteEntry}
                    onOpenChange={() => setDeleteEntry(null)}
                    title={t.delete_dialog.title}
                    description={t.delete_dialog.description}
                    confirmText={t.delete_dialog.confirm}
                    cancelText={t.delete_dialog.cancel}
                    onConfirm={() => {
                        if (!deleteEntry) return;
                        router.delete(`/history_entries/${deleteEntry.id}`);
                        setDeleteEntry(null);
                    }}
                />
            </div>
        </AuthorizedLayout>
    );
}